// client/src/components/react-dashboard/data_assets/EditDataAsset.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    DialogActions,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../../theme';
import { useUpdateDataAssetMutation } from '../../../features/data_assets/dataAssetsApiSlice';

const RELEASE_OPTIONS = [
    { label: 'None', value: '' },
    { label: 'Contact', value: 'Contact' },
    { label: 'Digital', value: 'Digital' },
];

const S3_FORMATS = ['csv', 'parquet'];

const getVersionRowId = (v, i) => v._id || v.id || `${i}`;

const getS3UriForFormat = (version, format) => {
    const locations = Array.isArray(version?.s3_locations)
        ? version.s3_locations
        : [];

    const match = locations.find(
        (loc) => String(loc.format || '').toLowerCase() === format
    );

    if (match?.s3_uri) return match.s3_uri;

    // Backward compatibility for old assets that still have version.s3_uri
    if (format === 'csv' && version?.s3_uri) return version.s3_uri;

    return '';
};

const cleanDateOrNull = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
};

const buildS3LocationsFromRow = (existingVersion, row) => {
    const existingLocations = Array.isArray(existingVersion?.s3_locations)
        ? existingVersion.s3_locations
        : [];

    const existingByFormat = new Map(
        existingLocations
            .filter((loc) => loc?.format)
            .map((loc) => [String(loc.format).toLowerCase(), loc])
    );

    return S3_FORMATS.map((format) => {
        const uri = String(row?.[`${format}_s3_uri`] || '').trim();
        if (!uri) return null;

        const existing = existingByFormat.get(format) || {};

        return {
            ...existing,
            format,
            s3_uri: uri,
            timestamp_utc: cleanDateOrNull(existing.timestamp_utc) || undefined,
        };
    }).filter(Boolean);
};

const normalizeVersionForSave = (v) => {
    const s3Locations = Array.isArray(v.s3_locations)
        ? v.s3_locations
            .map((loc) => {
                const format = String(loc?.format || '').toLowerCase();
                const uri = String(loc?.s3_uri || '').trim();

                if (!S3_FORMATS.includes(format) || !uri) return null;

                return {
                    ...(loc._id ? { _id: loc._id } : {}),
                    format,
                    s3_uri: uri,
                    timestamp_utc: cleanDateOrNull(loc.timestamp_utc) || undefined,
                };
            })
            .filter(Boolean)
        : [];

    // Backward compatibility: old version.s3_uri becomes csv location
    if (!s3Locations.some((loc) => loc.format === 'csv') && v.s3_uri) {
        s3Locations.push({
            format: 'csv',
            s3_uri: String(v.s3_uri).trim(),
        });
    }

    return {
        ...(v._id ? { _id: v._id } : {}),
        version: v.version ?? '',
        snowflake_table: v.snowflake_table ?? '',
        s3_locations: s3Locations,
        timestamp_utc: cleanDateOrNull(v.timestamp_utc),
    };
};

const boolToString = (v) => (v === true ? 'true' : v === false ? 'false' : '');
const stringToBool = (v) => (v === 'true' ? true : v === 'false' ? false : undefined);

/**
 * EditDataAsset (UI-only demo)
 * Props:
 *  - asset: the selected data asset object
 *  - onClose: () => void
 *  - onSave: (nextAsset) => void   // optional, lets parent update local state or call mutation
 */
const EditDataAsset = ({ asset, onClose, onSave }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [draft, setDraft] = useState(null);
    const [updateDataAsset, { isLoading: saving }] = useUpdateDataAssetMutation();
    const [saveError, setSaveError] = useState('');

    useEffect(() => {
        if (!asset) return;
        // Deep-ish copy for editing
        setDraft(JSON.parse(JSON.stringify(asset)));
    }, [asset]);

    const isStandard = draft?.assetType === 'Standard Delivery';

    const update = (path, value) => {
        setDraft((prev) => {
            const next = { ...prev };
            const keys = path.split('.');
            let cur = next;
            for (let i = 0; i < keys.length - 1; i++) {
                const k = keys[i];
                cur[k] = cur[k] ?? {};
                cur = cur[k];
            }
            cur[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const versionRows = useMemo(() => {
        const versions = draft?.versions || [];

        return versions.map((v, i) => ({
            id: getVersionRowId(v, i),
            _id: v._id,
            version: v.version ?? '',
            csv_s3_uri: getS3UriForFormat(v, 'csv'),
            parquet_s3_uri: getS3UriForFormat(v, 'parquet'),
            snowflake_table: v.snowflake_table ?? '',
            timestamp_utc: v.timestamp_utc ?? null,
        }));
    }, [draft]);

    const toDatetimeLocal = (value) => {
        if (!value) return '';
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return '';
        // datetime-local wants "YYYY-MM-DDTHH:mm"
        const pad = (n) => String(n).padStart(2, '0');
        const yyyy = d.getFullYear();
        const mm = pad(d.getMonth() + 1);
        const dd = pad(d.getDate());
        const hh = pad(d.getHours());
        const mi = pad(d.getMinutes());
        return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
    };

    const fromDatetimeLocalToIso = (value) => {
        // value like "2026-03-03T11:26"
        if (!value) return null;
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return null;
        return d.toISOString();
    };

    const versionColumns = [
        {
            field: 'version',
            headerName: 'Version',
            width: 120,
            editable: true,
        },
        {
            field: 'csv_s3_uri',
            headerName: 'CSV S3 URI',
            flex: 1,
            minWidth: 240,
            editable: true,
        },
        {
            field: 'parquet_s3_uri',
            headerName: 'Parquet S3 URI',
            flex: 1,
            minWidth: 240,
            editable: true,
        },
        {
            field: 'snowflake_table',
            headerName: 'Snowflake Table',
            flex: 1,
            minWidth: 220,
            editable: true,
        },
        {
            field: 'timestamp_utc',
            headerName: 'Added',
            width: 190,
            editable: true,
            valueFormatter: (p) => {
                const v = p.value;
                if (!v) return '';
                const d = new Date(v);
                if (Number.isNaN(d.getTime())) return String(v);
                return d.toLocaleString();
            },
            renderEditCell: (params) => {
                const current = toDatetimeLocal(params.value);
                return (
                    <TextField
                        size="small"
                        type="datetime-local"
                        value={current}
                        onChange={(e) => {
                            const iso = fromDatetimeLocalToIso(e.target.value);
                            params.api.setEditCellValue({
                                id: params.id,
                                field: params.field,
                                value: iso,
                            });
                        }}
                        sx={{ width: '100%' }}
                        inputProps={{ step: 60 }}
                    />
                );
            },

            sortable: false,
        },
        {
            field: 'actions',
            headerName: '',
            width: 110,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Button
                    size="small"
                    color="error"
                    onClick={() => {
                        const rowId = params.row._id || params.id;
                        setDraft(prev => {
                            const next = { ...prev };
                            next.versions = (next.versions || []).filter(v => String(v._id || '') !== String(rowId));
                            return next;
                        });
                    }}
                >
                    Delete
                </Button>
            ),
        },
    ];

    const handleVersionEditCommit = (params) => {
        const { id: rowId, field, value } = params;

        setDraft((prev) => {
            const next = { ...prev };
            const versions = [...(next.versions || [])];
            const idx = versions.findIndex(v => String(v._id || '') === String(rowId));
            if (idx === -1) return prev;

            versions[idx] = { ...(versions[idx] || {}), [field]: value };
            next.versions = versions;
            return next;
        });
    };

    if (!draft) {
        return (
            <Box p={2}>
                <Typography variant="body2" color={colors.grey[300]}>
                    No asset selected
                </Typography>
            </Box>
        );
    }

    const headerChip = () => {
        if (!isStandard) return <Chip size="small" label="Real-Time" />;
        if (!draft.release) return <Chip size="small" label="Standard (No Release)" />;
        return <Chip size="small" label={`Release: ${draft.release}`} />;
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Edit Data Asset
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                        UI demo — wire onSave to a mutation when ready.
                    </Typography>
                </Box>
                {headerChip()}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Basic fields */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: colors.primary[300] }}>
                    Asset Details
                </Typography>

                <Stack spacing={2}>
                    <TextField
                        label="Name"
                        value={draft.name || ''}
                        onChange={(e) => update('name', e.target.value)}
                        size="small"
                        fullWidth
                    />

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Usage</InputLabel>
                            <Select
                                label="Usage"
                                value={draft.usage || ''}
                                onChange={(e) => update('usage', e.target.value)}
                            >
                                <MenuItem value="Internal">Internal</MenuItem>
                                <MenuItem value="External">External</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" fullWidth>
                            <InputLabel>Asset Type</InputLabel>
                            <Select
                                label="Asset Type"
                                value={draft.assetType || ''}
                                onChange={(e) => update('assetType', e.target.value)}
                            >
                                <MenuItem value="Real-Time">Real-Time</MenuItem>
                                <MenuItem value="Standard Delivery">Standard Delivery</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" fullWidth disabled={!isStandard}>
                            <InputLabel>Release</InputLabel>
                            <Select
                                label="Release"
                                value={draft.release || ''}
                                onChange={(e) => update('release', e.target.value || undefined)}
                            >
                                {RELEASE_OPTIONS.map((o) => (
                                    <MenuItem key={o.label} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Stack>
            </Paper>

            {/* Rebuild fields */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: colors.primary[300] }}>
                    Rebuild
                </Typography>

                <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Rebuilt</InputLabel>
                            <Select
                                label="Rebuilt"
                                value={boolToString(draft?.rebuild?.isRebuilt)}
                                onChange={(e) => update('rebuild.isRebuilt', stringToBool(e.target.value))}
                            >
                                <MenuItem value="">(unset)</MenuItem>
                                <MenuItem value="true">true</MenuItem>
                                <MenuItem value="false">false</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            label="Frequency"
                            value={draft?.rebuild?.frequency || ''}
                            onChange={(e) => update('rebuild.frequency', e.target.value)}
                            size="small"
                            fullWidth
                        />

                        <TextField
                            label="Time"
                            value={draft?.rebuild?.time || ''}
                            onChange={(e) => update('rebuild.time', e.target.value)}
                            size="small"
                            fullWidth
                            placeholder="e.g. 03:00"
                        />
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                            label="Day"
                            value={draft?.rebuild?.day ?? ''}
                            onChange={(e) => update('rebuild.day', e.target.value === '' ? undefined : Number(e.target.value))}
                            size="small"
                            fullWidth
                            type="number"
                        />
                        <TextField
                            label="Day of Week"
                            value={draft?.rebuild?.dayOfWeek || ''}
                            onChange={(e) => update('rebuild.dayOfWeek', e.target.value)}
                            size="small"
                            fullWidth
                            placeholder="e.g. Monday"
                        />
                        <TextField
                            label="Cron"
                            value={draft?.rebuild?.cronSchedule || ''}
                            onChange={(e) => update('rebuild.cronSchedule', e.target.value)}
                            size="small"
                            fullWidth
                        />
                    </Stack>
                </Stack>
            </Paper>

            {/* Versions */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: colors.primary[300] }}>
                    Versions (Edit existing)
                </Typography>

                <Box sx={{ height: 280 }}>
                    <DataGrid
                        rows={versionRows}
                        columns={versionColumns}
                        density="compact"
                        disableSelectionOnClick
                        hideFooterSelectedRowCount
                        disableColumnMenu
                        processRowUpdate={(newRow) => {
                            const rowId = newRow._id || newRow.id;

                            setDraft((prev) => {
                                const next = { ...prev };
                                const versions = [...(next.versions || [])];

                                const idx = versions.findIndex(
                                    (v, i) => String(getVersionRowId(v, i)) === String(rowId)
                                );

                                if (idx === -1) return prev;

                                const existingVersion = versions[idx];

                                versions[idx] = {
                                    ...existingVersion,
                                    version: newRow.version,
                                    snowflake_table: newRow.snowflake_table,
                                    timestamp_utc: newRow.timestamp_utc ?? null,
                                    s3_locations: buildS3LocationsFromRow(existingVersion, newRow),
                                };

                                // Remove old legacy field if it exists
                                delete versions[idx].s3_uri;

                                next.versions = versions;
                                return next;
                            });

                            return newRow;
                        }}
                        onProcessRowUpdateError={(err) => {
                            console.error(err);
                        }}
                        experimentalFeatures={{ newEditingApi: true }}
                        sx={{
                            fontSize: '0.8rem',
                            '.MuiDataGrid-cell': { py: 0.5, px: 0.5 },
                            '.MuiDataGrid-columnHeader': { py: 0.5, px: 0.5 },
                        }}
                    />
                </Box>

                <Typography variant="caption" sx={{ color: colors.grey[300], mt: 1, display: 'block' }}>
                    Tip: keep using “Add Version” from the Asset View for adding new versions; this section is for quick edits / cleanup.
                </Typography>
            </Paper>
            {saveError ? (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {saveError}
                </Typography>
            ) : null}
            <DialogActions sx={{ mt: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={saving}
                    onClick={async () => {
                        setSaveError('');
                        try {
                            const normalizedVersions = (draft.versions || []).map(normalizeVersionForSave);
                            const updated = await updateDataAsset({
                                id: draft._id || draft.id,
                                name: draft.name,
                                usage: draft.usage,
                                assetType: draft.assetType,
                                release: draft.assetType === 'Standard Delivery' ? (draft.release || '') : '',

                                s3_uris: draft.s3_uris || [],
                                snowflake_tables: draft.snowflake_tables || [],
                                updateCadence: draft.updateCadence || {},
                                versions: normalizedVersions,
                                countsStats: draft.countsStats || [],
                                rebuild: draft.rebuild || {},
                            }).unwrap();

                            // ✅ notify parent so it can refetch or update local selection
                            onSave?.(updated);

                            onClose?.();
                        } catch (e) {
                            setSaveError(e?.data?.message || 'Failed to save changes');
                        }
                    }}
                    sx={{
                        backgroundColor: colors.greenAccent[500],
                        color: colors.grey[100],
                        '&:hover': { backgroundColor: colors.greenAccent[400] },
                    }}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Box>
    );
};

export default EditDataAsset;